import { set, sortBy } from 'lodash/fp';

import { getSchemaForFieldPath } from 'dashboard/components/JsonSchemaSelect/utils';
import { getJsonSchemaType } from 'dashboard/lib/workflows';
import { CaseDataManagerSchema, CaseDataManagerSchemaKeys } from 'dashboard/models/case-template';
import { FieldSchema, getJsonSchemaForFieldSchema, JsonSchemaAllTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

export type Option = {
  label: string;
  value: string;
  property: string;
  schema: JsonSchema;
};

const isFieldSchema = (schema: FieldSchema): schema is FieldSchema => {
  return (
    schema &&
    'key' in schema &&
    'label' in schema &&
    'type' in schema &&
    'default-value' in schema &&
    'config' in schema
  );
};

const generateCaseDataManagerJsonSchemaHelper = (
  objectSchema: Record<string, any>,
  fieldDescription: string,
): JsonSchema => {
  const properties = Object.entries(objectSchema).reduce<Record<string, JsonSchema>>(
    (hash, [key, value]) => {
      if (isFieldSchema(value)) {
        return {
          ...hash,
          [key]: getJsonSchemaForFieldSchema(value),
        };
      } else {
        return {
          ...hash,
          [key]: generateCaseDataManagerJsonSchemaHelper(value, key),
        };
      }
    },
    {},
  );

  return {
    type: JsonSchemaTypes.Object,
    description: fieldDescription,
    properties,
  };
};

// Recursively generates JSON schemas for nested objects. (e.g. case.case-template)
export const generateCaseDataManagerJsonSchema = (
  caseDataManagerSchema: CaseDataManagerSchema,
): JsonSchemaObject => {
  const properties = Object.keys(caseDataManagerSchema).reduce(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (hash, key: CaseDataManagerSchemaKeys) => {
      const jsonSchema = generateCaseDataManagerJsonSchemaHelper(caseDataManagerSchema[key], key);
      return set(key, jsonSchema, hash);
    },
    {},
  );
  return {
    type: JsonSchemaTypes.Object,
    description: 'Case Data Manager',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    properties,
  };
};

export const dotNotationFormatter = (current: string, appendText: string) => {
  if (!current) {
    return appendText;
  }
  return `${current}.${appendText}`;
};

export const getSchemaOptions = (option: Partial<Option>): Option[] => {
  const { value, schema } = option;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const schemaType = getJsonSchemaType(schema);

  const schemaOptions = (() => {
    switch (schemaType) {
      case JsonSchemaAllTypes.Object: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return Object.entries(schema.properties || {}).reduce((options, [prop, propSchema]) => {
          // This should be replaced by passing in a filter to hide case.fields if it has no properties
          // NB: we assume that if there are no nested properties for a JSON object, we can't display it.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const nestedProperties = Object.keys(propSchema?.properties ?? {});
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          if (propSchema?.type === 'object' && Object.keys(nestedProperties).length === 0) {
            return options;
          }
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const fullValue = dotNotationFormatter(value, prop);
          return [
            ...options,
            {
              label: fullValue,
              value: fullValue,
              property: prop,
              schema: propSchema as JsonSchema,
            },
          ];
        }, []);
      }
      case JsonSchemaAllTypes.Array:
      case JsonSchemaAllTypes.String:
      case JsonSchemaAllTypes.Integer:
      case JsonSchemaAllTypes.Number:
      case JsonSchemaAllTypes.Boolean:
      case JsonSchemaAllTypes.Date:
      case JsonSchemaAllTypes.Enum:
      case JsonSchemaAllTypes.File:
      case JsonSchemaAllTypes.MultiEnum:
      case JsonSchemaAllTypes.GroupedEnum:
      case JsonSchemaAllTypes.Relation:
      case JsonSchemaAllTypes.Unknown:
        return [];
      default:
        assertNever(schemaType);
    }
  })();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return sortBy('property', schemaOptions);
};

export const validatePath = (path: string, jsonSchema: JsonSchema) => {
  const progressivePaths = getProgressivePaths(path);
  let valid = true;
  let index = 0;
  let schemaOptions = getSchemaOptions({ schema: jsonSchema });

  while (progressivePaths[index] && valid && schemaOptions.length) {
    const current = progressivePaths[index];
    const pathValue = current.endsWith('.') ? current.slice(0, -1) : current;
    const optionPartialMatch = schemaOptions.find((option) => option.value.includes(pathValue));
    const optionMatch = schemaOptions.find((option) => option.value === pathValue);

    if (!optionPartialMatch || (current.endsWith('.') && !optionMatch)) {
      valid = false;
      break;
    }

    index++;
    if (current.endsWith('.') && optionMatch) {
      schemaOptions = getSchemaOptions(optionMatch);
    }
  }

  return {
    valid,
    schemaOptions,
  };
};

export const getProgressivePaths = (path: string) => {
  const periodIndices = path.split('').reduce((indices, char, i) => {
    if (char === '.') {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      indices.push(i);
    }
    return indices;
  }, []);

  const paths: string[] = [];
  if (periodIndices.length) {
    periodIndices.forEach((periodIndex) => paths.push(path.slice(0, periodIndex + 1)));
    if (!path.endsWith('.')) {
      paths.push(path);
    }
  } else {
    paths.push(path);
  }

  return paths;
};

export const getOptionForValue = (value: string, jsonSchema: JsonSchema): Option => {
  const { schema } = getSchemaForFieldPath({ path: value, initialSchema: jsonSchema });
  if (!schema) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }

  return {
    label: value,
    value: value,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    property: value.split('.').pop(),
    schema,
  };
};
